import type { Contact, OptionsDocument, PublicQuoteContact } from 'paintscout'
import type { Owner } from '@paintscout/api'
import { mapPublicToLeadSource, mapPublicToTags } from '../../../options'
import { formatPhoneNumber, uuid } from '../../../util'
import { mapPublicToPreferredContactMethod } from '../../../util/format-preferred-contact'

export function mapPublicToContact({
  contact,
  previousContact,
  options,
  owner
}: {
  contact: PublicQuoteContact
  previousContact?: Contact
  options: OptionsDocument
  owner?: Owner
}): Contact {
  const now = Date.now()
  const sanitizedEmail = contact.email ? contact.email.replace(/\s/g, '') : previousContact?.email ?? ''
  const contactOwner = {
    ...previousContact?.owner,
    ...contact?.owner,
    ...owner
  }

  // email characters are complicated - https://stackoverflow.com/questions/26855757/remove-invalid-characters-from-email
  return {
    _id: contact?.id || uuid(),
    _rev: contact?.rev ?? previousContact?._rev ?? null,
    type: 'contact',
    contact_type: 'contact',
    first_name: contact?.firstName ?? previousContact?.first_name ?? '',
    last_name: contact?.lastName ?? previousContact?.last_name ?? '',
    secondary_first_name: contact?.secondaryFirstName ?? previousContact?.secondary_first_name ?? '',
    secondary_last_name: contact?.secondaryLastName ?? previousContact?.secondary_last_name ?? '',
    company: contact?.company ?? previousContact?.company ?? '',
    email: sanitizedEmail,
    alternate_email: contact?.alternateEmail ?? previousContact?.alternate_email ?? '',
    phone_number: formatPhoneNumber({
      options,
      phoneNumber: contact?.phoneNumber ?? previousContact?.phone_number ?? ''
    }),
    alternate_phone: formatPhoneNumber({
      options,
      phoneNumber: contact?.alternatePhoneNumber ?? previousContact?.alternate_phone ?? ''
    }),
    address: contact?.address ?? previousContact?.address ?? '',
    city: contact?.city ?? previousContact?.city ?? '',
    province: contact?.state ?? previousContact?.province ?? '',
    postal: contact?.zip ?? previousContact?.postal ?? '',
    internal_note: contact?.note ?? previousContact?.internal_note ?? '',
    source_id: contact?.source?.id ?? previousContact?.source_id ?? '',
    source_user_id: contact?.source?.userId ?? previousContact?.source_user_id ?? '',
    source: contact?.source?.provider ?? previousContact?.source ?? '',
    lead_source: mapPublicToLeadSource({
      options,
      leadSource: contact?.leadSource ?? previousContact?.lead_source ?? ''
    }),
    owner: {
      ...contactOwner
    },
    assignedUsers: contactOwner?.id
      ? {
          [contactOwner.id]: {
            assigned: true
          }
        }
      : null,
    created_by: previousContact?.created_by ?? null,
    created_date: previousContact?.created_date ?? now,
    updated_date: now,
    preferred_contact_method: contact?.preferredContactMethod
      ? mapPublicToPreferredContactMethod(contact.preferredContactMethod)
      : previousContact?.preferred_contact_method ?? '',
    tags: contact?.tags ? mapPublicToTags({ options, tags: contact.tags }) : previousContact?.tags ?? [],
    ...(previousContact?.files && { files: previousContact.files })
  }
}
